import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    OnInit
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DecimalPipe } from "@angular/common";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { catchError, EMPTY, exhaustMap, filter, interval } from "rxjs";

import { NavbarService } from "../../../../../layouts/tenant-interactive-layout/navbar.service";
import { FlipService } from "../../../../../shared/services/flip.service";
import { SnackbarService } from "../../../../../components/snackbar/snackbar.service";
import { PollResponse, PollResultItem, PollResultsResponse } from "../../../../models/poll.model";
import { PollResultsTableComponent } from "../../../components/poll-results-table/poll-results-table.component";
import { HostApiService } from "../host.api.service";

@Component({
    selector: "app-host-poll",
    templateUrl: "./host-poll.component.html",
    styleUrl: "./host-poll.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    providers: [FlipService],
    imports: [CdkScrollable, DecimalPipe, PollResultsTableComponent]
})
export class HostPollComponent implements OnInit {
    private readonly hostApiService = inject(HostApiService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly snackbarService = inject(SnackbarService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly navbarService = inject(NavbarService);
    private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly flip = inject(FlipService);
    private readonly interactiveId$ = this.navbarService.selectedInteractiveIds$.pipe(filter(ids => ids.length > 0));

    // How often results are re-fetched so the host sees votes land without a manual refresh.
    private static readonly POLL_INTERVAL_MS = 7500;

    protected poll: PollResponse | null = null;
    protected results: PollResultsResponse | null = null;
    protected tenantLogoUrl: string | null = null;
    protected hostViewWallpaperUrl: string | null = null;

    // True once the first results have rendered, so the initial paint never animates.
    private hasLoaded = false;

    ngOnInit(): void {
        this.interactiveId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.getHostPollData());
        this.setupPolling();
    }

    getHostPollData(): void {
        const tenantId = this.navbarService.tenantId();
        const interactiveId = this.navbarService.interactiveId();
        if (!tenantId || !interactiveId) {
            this.showError();
            return;
        }

        this.hostApiService.getHostPollViewData(tenantId, interactiveId).subscribe({
            next: ({ poll, results, theme }) => {
                this.poll = poll;
                this.results = results;
                this.hasLoaded = true;
                this.tenantLogoUrl = theme.themeConfig?.tenantLogoUrl ?? null;
                this.hostViewWallpaperUrl = theme.themeConfig?.hostViewWallpaperUrl ?? null;
                this.changeDetectorRef.markForCheck();
            },
            error: () => this.showError()
        });
    }

    private setupPolling(): void {
        if (!this.navbarService.tenantId() || !this.navbarService.interactiveId()) return;

        interval(HostPollComponent.POLL_INTERVAL_MS)
            .pipe(
                filter(() => !this.flip.isAnimating),
                exhaustMap(() =>
                    this.hostApiService
                        // A toast on every failed tick would be unusable on a view that refreshes
                        // for the length of a show, so a bad tick is swallowed and the next one retries.
                        .getPollResults(this.navbarService.interactiveId())
                        .pipe(catchError(() => EMPTY))
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(results => this.applyResults(results));
    }

    /**
     * Adopt a refreshed set of results. A tick that brings nothing new is dropped entirely — no
     * re-render, no sweep — so the green sweep means "the numbers just moved" rather than "a
     * request completed". On a quiet poll the view therefore sits still between real changes.
     */
    private applyResults(next: PollResultsResponse): void {
        const changed = !this.results || this.resultsSignature(this.results) !== this.resultsSignature(next);
        if (!changed) return;

        this.reorderRows(() => (this.results = next));
        if (this.hasLoaded) this.triggerSyncPulse();
    }

    /**
     * Serialize the ranked list to compare fetches: id captures membership and order, count
     * captures the vote movement that reorders rows and resizes bars. Percentages are derived
     * from the counts, so they add nothing here.
     */
    private resultsSignature(results: PollResultsResponse): string {
        return JSON.stringify(results.results.map((item: PollResultItem) => [item.itemId, item.count]));
    }

    /** Animate rows from their old positions to their new ones as the ranking changes. */
    private reorderRows(mutate: () => void): void {
        const animated = this.flip.animate({
            container: this.host.nativeElement,
            itemSelector: "tr.mat-mdc-row",
            key: el => el.dataset["itemId"] ?? null,
            mutate,
            render: () => this.changeDetectorRef.detectChanges()
        });
        if (!animated) this.changeDetectorRef.markForCheck();
    }

    /** Fill the success progress line across the top of the results bar on each sync. */
    private triggerSyncPulse(): void {
        const el = this.host.nativeElement.querySelector<HTMLElement>(".sync-sweep");
        if (!el) return;

        el.classList.remove("active");
        el.getBoundingClientRect(); // force reflow so the animation restarts on every sync
        el.classList.add("active");
    }

    showError(): void {
        this.snackbarService.show("Uh oh, something's gone wrong.", "error");
    }
}
